import type { ChangeEvent } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';

export type InputMode = 'amount' | 'percentage';

export interface UseAmountPercentageToggleOptions {
  /**
   * Current percentage value (e.g., 5.0 for 5%)
   */
  percentageValue: number;

  /**
   * Current amount value (e.g., 5000 for £5000)
   */
  amountValue: number;

  /**
   * Which mode is currently active
   */
  activeMode: InputMode;

  /**
   * Callback when value changes
   * Returns the new value and which type it is
   */
  onChange: (value: number, type: InputMode) => void;
}

export interface UseAmountPercentageToggleReturn {
  /**
   * Current input mode (amount or percentage)
   */
  inputMode: InputMode;

  /**
   * Display value as string (what the user sees in the input)
   */
  displayValue: string;

  /**
   * Handler for input change events
   */
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void;

  /**
   * Toggle between amount and percentage modes
   */
  handleModeToggle: () => void;
}

/**
 * Custom hook for managing amount/percentage toggle input behavior
 *
 * This hook manages TWO INDEPENDENT values:
 * - percentageValue: The percentage value (e.g., 5 for 5%)
 * - amountValue: The absolute amount value (e.g., 5000 for £5000)
 *
 * When user toggles between modes, NO CONVERSION happens.
 * The inactive field's value is simply not sent to the backend.
 *
 * @example
 * ```tsx
 * const { inputMode, displayValue, handleInputChange, handleModeToggle } =
 *   useAmountPercentageToggle({
 *     percentageValue: 5.0,
 *     amountValue: 10000,
 *     activeMode: 'percentage',
 *     onChange: (val, type) => {
 *       if (type === 'percentage') {
 *         setPercentageValue(val);
 *       } else {
 *         setAmountValue(val);
 *       }
 *     },
 *   });
 * ```
 */
export function useAmountPercentageToggle({
  percentageValue,
  amountValue,
  activeMode,
  onChange,
}: UseAmountPercentageToggleOptions): UseAmountPercentageToggleReturn {
  const [inputMode, setInputMode] = useState<InputMode>(activeMode);
  const [displayValue, setDisplayValue] = useState<string>('0');

  // Sync input mode with activeMode prop
  useEffect(() => {
    setInputMode(activeMode);
  }, [activeMode]);

  // Memoize the computed display value to avoid unnecessary recalculations
  const computedDisplayValue = useMemo(() => {
    if (inputMode === 'percentage') {
      return (percentageValue ?? 0).toFixed(1);
    }
    return (amountValue ?? 0).toString();
  }, [percentageValue, amountValue, inputMode]);

  // Update display value when computed value changes
  useEffect(() => {
    setDisplayValue(computedDisplayValue);
  }, [computedDisplayValue]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);

    const numericValue = parseFloat(inputValue) || 0;

    if (inputMode === 'percentage') {
      // User entered a percentage - store it as percentage
      onChange(numericValue, 'percentage');
    } else {
      // User entered an amount - store it as amount (rounded)
      onChange(Math.round(numericValue), 'amount');
    }
  }, [inputMode, onChange]);

  const handleModeToggle = useCallback((): void => {
    // Simply toggle the mode - NO conversion
    const newMode = inputMode === 'amount' ? 'percentage' : 'amount';
    setInputMode(newMode);

    // Notify parent of the mode change with the current value of that mode
    if (newMode === 'percentage') {
      onChange(percentageValue, 'percentage');
    } else {
      onChange(amountValue, 'amount');
    }
  }, [inputMode, percentageValue, amountValue, onChange]);

  return {
    inputMode,
    displayValue,
    handleInputChange,
    handleModeToggle,
  };
}
